import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import CustomButton from "components/common/Button/CustomButton";
import FileUploadInput from "components/common/FileUpload/FileUploadInput";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import { toast } from "react-toastify";
import { apiCall } from "store/services/service";
import { BASE_URL, URL_HRM_PARTICIPANT_UPLOAD_REQUIRED_DOCUMENT, URL_SP_PARTICIPANT_UPLOAD_REQUIRED_DOCUMENT } from "store/services/URL";
import { apiPostRequest } from "helpers/Requests";
import { useLocation, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { workerProfileDetailsFetchRequest } from "store/actions/sp.actions";
import { getUser } from "store/selector/auth.selector";
import {
  isFrontlineEmployeeUser,
  roleAccess,
} from "helpers/RolesPermission";
import { USER_ROLE } from "Constants/constant";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import GetItemFromLocalstorage from "Utils/GetItemFromLocalstorage";
import { parseBooleanVal, baseURLImage, sanitizeDocumentUrl } from "Utils/utils";
import { findDocumentComplianceSetting } from "./complianceSettingMatch";
const UploadFileTableFolder = (props) => {
  const {
    selectedFile,
    SelectedFileName,
    handleClose,
    setUpdatedFile,
    isOpen,
    workerId,
    fileScore,
    parentId,
    isUserGracePeriodOver,
    getEmployeeDocuments,
    getDocumentsListForProgressBar,
    requiredDocumentslist = [],
    onLoadingChange = null,

    //from container
    WorkerProfileDataRequest,
    currentLoginUser,

    //not use
    setParentId,
    role = null,
  } = props;
  const location = useLocation();
  const params = useParams();

  const { state } = location;

  const [file, setFile] = useState(null);
  const [issueDate, setIssueDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [showInput, setShowInput] = useState(false);
  const [isFileUploadToS3, setIsFileUploadToS3] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
      setIsFileUploadToS3(false);
    }
  }, [isOpen]);

  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [issueDateTouched, setIssueDateTouched] = useState(false);
  const [expiryDateTouched, setExpiryDateTouched] = useState(false);
  const [existingAttachmentRemoved, setExistingAttachmentRemoved] =
    useState(false);
  const hydratedDocumentKeyRef = useRef("");

  const isParticipantCompliance = role === "participant";

  const complianceSettingForDocument = useMemo(() => {
    if (!requiredDocumentslist?.length) {
      return null;
    }
    return findDocumentComplianceSetting(requiredDocumentslist, {
      uuid: selectedFile?.uuid,
      id: selectedFile?.id,
      name: SelectedFileName || selectedFile?.name,
    });
  }, [
    requiredDocumentslist,
    SelectedFileName,
    selectedFile?.uuid,
    selectedFile?.id,
    selectedFile?.name,
  ]);

  const requiresIssueDate = parseBooleanVal(
    selectedFile?.require_issue_date ??
      complianceSettingForDocument?.require_issue_date
  );
  const requiresExpiryDate = parseBooleanVal(
    selectedFile?.require_expiry_date ??
      complianceSettingForDocument?.require_expiry_date
  );
  const hasComplianceDateRequirements = requiresIssueDate || requiresExpiryDate;
  const showIssueDateField = isParticipantCompliance
    ? requiresIssueDate
    : requiresIssueDate || showInput;
  const showExpiryDateField = isParticipantCompliance
    ? requiresExpiryDate
    : requiresExpiryDate || showInput;
  const showOptionalDateCheckbox =
    !isParticipantCompliance && !hasComplianceDateRequirements;

  // Get SP Settings to check if comments are enabled - check multiple sources
  const [spSettingsState, setSpSettingsState] = useState(GetItemFromLocalstorage("spSettings"));
  const [userDataState, setUserDataState] = useState(GetItemFromLocalstorage("user"));
  
  // Update settings when modal opens to get latest values
  // Also update periodically to detect changes when toggle is turned off
  // This ensures settings are refreshed after logout/login and browser refresh
  useEffect(() => {
    const updateSettings = () => {
      setSpSettingsState(GetItemFromLocalstorage("spSettings"));
      setUserDataState(GetItemFromLocalstorage("user"));
    };
    
    if (isOpen) {
      // Update immediately when modal opens
      updateSettings();
      if (getDocumentsListForProgressBar) {
        getDocumentsListForProgressBar();
      }
      
      // Set up interval to check for settings changes every 1 second while modal is open
      const interval = setInterval(updateSettings, 1000);
      
      // Listen for storage events
      const handleStorageChange = (e) => {
        if (e.key === 'spSettings' || e.key === 'user') {
          updateSettings();
        }
      };
      window.addEventListener('storage', handleStorageChange);
      
      // Listen for custom event when user logs in (settings are refreshed)
      const handleLoginRefresh = () => {
        updateSettings();
      };
      window.addEventListener('userLoggedIn', handleLoginRefresh);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('userLoggedIn', handleLoginRefresh);
      };
    }
  }, [isOpen]);

  // Also refresh settings on component mount (after browser refresh or login)
  useEffect(() => {
    // Refresh settings immediately when component mounts
    setSpSettingsState(GetItemFromLocalstorage("spSettings"));
    setUserDataState(GetItemFromLocalstorage("user"));
  }, []);
  
  const spSettings = spSettingsState;
  const userData = userDataState;
  const serviceProviderSettings = userData?.service_provider || {};
  
  // Check both localStorage spSettings and user's service_provider object
  // Backend field name is 'document_comments' (confirmed by user)
  // Use parseBooleanVal to properly handle boolean/string/number values
  // CRITICAL: Same logic for both SP Admin and Manager - no role-specific differences
  // When toggle is OFF in SP Settings → allowDocumentComments = false → Comment box hidden
  // When toggle is ON in SP Settings → allowDocumentComments = true → Comment box visible
  const documentCommentsFromSpSettings = spSettings?.document_comments;
  const documentCommentsFromServiceProvider = serviceProviderSettings?.document_comments;
  
  // Check document_comments field from either source
  // Priority: serviceProviderSettings > spSettings (same as DocumentsPage.jsx)
  // Handle: true, 1, "1", "true", "yes" as true; false, 0, "0", "false", null, undefined as false
  // Explicitly check for false values first to ensure toggle OFF is recognized
  const isExplicitlyFalse = 
    documentCommentsFromSpSettings === false ||
    documentCommentsFromSpSettings === 0 ||
    documentCommentsFromSpSettings === "0" ||
    documentCommentsFromSpSettings === "false" ||
    documentCommentsFromServiceProvider === false ||
    documentCommentsFromServiceProvider === 0 ||
    documentCommentsFromServiceProvider === "0" ||
    documentCommentsFromServiceProvider === "false";
  
  const allowDocumentComments = isExplicitlyFalse ? false : (
    documentCommentsFromServiceProvider === true ||
    documentCommentsFromServiceProvider === 1 ||
    documentCommentsFromServiceProvider === "1" ||
    documentCommentsFromSpSettings === true ||
    documentCommentsFromSpSettings === 1 ||
    documentCommentsFromSpSettings === "1" ||
    parseBooleanVal(documentCommentsFromServiceProvider) || 
    parseBooleanVal(documentCommentsFromSpSettings) || 
    false
  );

  // Check if user can add/edit comments (SP Admin or Manager)
  const canEditComments = allowDocumentComments && roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]);

  const isComplianceDateEmpty = (v) =>
    v == null ||
    v === "" ||
    (typeof v === "string" && !String(v).trim());

  const showIssueDateRequiredError =
    requiresIssueDate &&
    isComplianceDateEmpty(issueDate) &&
    (hasAttemptedSubmit || issueDateTouched);
  const showExpiryDateRequiredError =
    requiresExpiryDate &&
    isComplianceDateEmpty(expiryDate) &&
    (hasAttemptedSubmit || expiryDateTouched);

  useEffect(() => {
    const today = moment().startOf("day");
    if (issueDate) {
      const formattedIssueDate = moment(issueDate).startOf("day");
      if (formattedIssueDate.isAfter(today)) {
        setError("Issue date should be less then or equal today");
        return;
      }
    }
    if (expiryDate) {
      const formattedExpiryDate = moment(expiryDate).startOf("day");
      if (!formattedExpiryDate.isAfter(today)) {
        setError("Expiry date cannot be a current or past date.");
        return;
      }
      if (
        issueDate &&
        moment(issueDate).startOf("day").isAfter(formattedExpiryDate)
      ) {
        setError("Expiry date cannot be earlier than issue date.");
        return;
      }
    }
    setError("");
  }, [issueDate, expiryDate]);

  useEffect(() => {
    if (!isOpen || !selectedFile) {
      hydratedDocumentKeyRef.current = "";
      setComment("");
      setShowInput(false);
      setIssueDate(null);
      setExpiryDate(null);
      setHasAttemptedSubmit(false);
      setIssueDateTouched(false);
      setExpiryDateTouched(false);
      setExistingAttachmentRemoved(false);
      return;
    }

    const documentKey = [
      selectedFile.docuemnt_id,
      selectedFile.document_id,
      selectedFile.id,
      selectedFile.name,
      selectedFile.link,
    ]
      .filter(Boolean)
      .join(":");

    // Only seed dates when the modal opens or a different document is selected.
    // Re-applying selectedFile.expire_date on later renders puts a cleared
    // mandatory expiry back into the field.
    if (hydratedDocumentKeyRef.current === documentKey) {
      return;
    }
    hydratedDocumentKeyRef.current = documentKey;
    setHasAttemptedSubmit(false);
    setIssueDateTouched(false);
    setExpiryDateTouched(false);
    setExistingAttachmentRemoved(false);

    if (isParticipantCompliance) {
      setShowInput(false);
    } else if (!hasComplianceDateRequirements) {
      setShowInput(Boolean(selectedFile?.expire_date || selectedFile?.issue_date));
    } else {
      setShowInput(true);
    }
    if (
      selectedFile?.expire_date &&
      (requiresExpiryDate || !isParticipantCompliance)
    ) {
      setExpiryDate(selectedFile.expire_date);
    } else if (isParticipantCompliance && !requiresExpiryDate) {
      setExpiryDate(null);
    } else if (!selectedFile?.expire_date) {
      setExpiryDate(null);
    }
    if (
      selectedFile?.issue_date &&
      (requiresIssueDate || !isParticipantCompliance)
    ) {
      setIssueDate(selectedFile.issue_date);
    } else if (isParticipantCompliance && !requiresIssueDate) {
      setIssueDate(null);
    } else if (!selectedFile?.issue_date) {
      setIssueDate(null);
    }
    if (selectedFile?.comment) {
      setComment(selectedFile.comment);
    } else {
      setComment("");
    }
  }, [
    isOpen,
    selectedFile,
    hasComplianceDateRequirements,
    requiresExpiryDate,
    requiresIssueDate,
    isParticipantCompliance,
  ]);

  const existingUploadedFile = useMemo(() => {
    const rawLink = selectedFile?.link;
    if (!rawLink && !selectedFile?.docuemnt_id) {
      return null;
    }
    const isAbsolute =
      typeof rawLink === "string" && /^https?:\/\//i.test(rawLink);
    const link = rawLink
      ? isAbsolute
        ? rawLink
        : sanitizeDocumentUrl(baseURLImage(rawLink))
      : null;
    return {
      ...selectedFile,
      link,
    };
  }, [selectedFile]);

  const existingUploadedFileName =
    SelectedFileName || selectedFile?.name || "Uploaded document";

  const issueDateValue = useMemo(
    () => (issueDate ? moment(issueDate).toDate() : null),
    [issueDate]
  );
  const expiryDateValue = useMemo(
    () => (expiryDate ? moment(expiryDate).toDate() : null),
    [expiryDate]
  );
  const minExpiryDate = useMemo(() => {
    const nextDay = new Date();
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(0, 0, 0, 0);
    return nextDay;
  }, []);

  const resolveWithExpiryPayload = () => {
    if (isParticipantCompliance) {
      return requiresIssueDate || requiresExpiryDate;
    }
    if (hasComplianceDateRequirements) {
      return (
        requiresIssueDate ||
        requiresExpiryDate ||
        Boolean(issueDate || expiryDate)
      );
    }
    return showInput && Boolean(issueDate || expiryDate);
  };

  const resolvePayloadDates = () => {
    const withExpiry = resolveWithExpiryPayload();
    if (!withExpiry) {
      return {
        with_expiry: false,
        issue_date: null,
        expiry_date: null,
      };
    }

    return {
      with_expiry: true,
      issue_date:
        showIssueDateField && !isComplianceDateEmpty(issueDate)
          ? issueDate
          : null,
      expiry_date:
        showExpiryDateField && !isComplianceDateEmpty(expiryDate)
          ? expiryDate
          : null,
    };
  };

  const canSaveDatesWithoutNewFile = Boolean(selectedFile?.docuemnt_id);
  const mandatoryFileMissingAfterRemoval =
    canSaveDatesWithoutNewFile &&
    existingAttachmentRemoved &&
    (file == null || file === "");
  const showMandatoryFileRequiredError =
    hasAttemptedSubmit && mandatoryFileMissingAfterRemoval;

  const modalFooter = () => (
    <>
      <CustomButton
        label='Cancel'
        variant='secondary'
        isDisabled={isLoading}
        clickHandler={() => {
          if (isLoading) return;
          handleClear();
          setFile("");
          setSelectedDocument("");
          setComment("");
          handleClose();
          const buttonRef = document.getElementById("cancelBTN");
          if (buttonRef) {
            buttonRef.click();
          }
        }}
      />
      <CustomButton
        showLoading={isLoading}
        isDisabled={
          ((file == null || file === "") && !canSaveDatesWithoutNewFile) ||
          isLoading ||
          isUserGracePeriodOver ||
          error?.length > 0
        }
        label={
          canSaveDatesWithoutNewFile && (file == null || file === "")
            ? "Update"
            : "Upload"
        }
        variant='primary'
        clickHandler={handleSubmitForFile}
      />
    </>
  );

  const [errorUploadAPI, setErrorUploadAPI] = useState("");

  const handleFileUploadBaseVapour = async (data) => {
    // Required-document upload only: do NOT validate SelectedFileName here. It is the document
    // type name from the system/backend (e.g. "Working With Children's Check"), which may contain
    // apostrophes, parentheses, etc. Name validation (letters, numbers, hyphen, underscore, space
    // only) remains in place for: CreateFolderModel, UploadNewFileModel, EditModel, EditFileModel,
    // CaseNotesFileModal (HRM, Participant, and MGT/SIL document modules).
    if (requiresIssueDate && isComplianceDateEmpty(issueDate)) {
      setIsLoading(false);
      setIsFileUploadToS3(false);
      toast.error("Issue Date is required.");
      return;
    }
    if (requiresExpiryDate && isComplianceDateEmpty(expiryDate)) {
      setIsLoading(false);
      setIsFileUploadToS3(false);
      toast.error("Expiry Date is required.");
      return;
    }

    Object.assign(data, {
      worker_id: role === "participant" ? undefined : workerId,
      pt_id: role === "participant" ? workerId : undefined,
      file_type: "Screening",
      document_type_id: 2,
      label: "",
      note: "xyz",
      name: (SelectedFileName || "").trim(),
      folder_structure_id: parentId,
      score: fileScore,
      ...resolvePayloadDates(),
    });

    // Include comment fields in upload payload if toggle is enabled, user has permission, and comment is provided
    // Backend expects: comment, commented_by, and commented_at fields in upload-required-document endpoint
    if (allowDocumentComments && canEditComments && comment) {
      Object.assign(data, {
        comment: comment,
        commented_by: currentLoginUser?.uuid || currentLoginUser?.id || null,
        commented_at: moment().toISOString(), // Current timestamp in ISO format
      });
    }

    if (selectedFile && selectedFile?.docuemnt_id) {
      Object.assign(data, {
        document_id: selectedFile.docuemnt_id,
      });
    }

    // setIsLoading(true);
    let URL = "";
    if (role === "participant") {
      URL = isFrontlineEmployeeUser()
        ? URL_HRM_PARTICIPANT_UPLOAD_REQUIRED_DOCUMENT
        : URL_SP_PARTICIPANT_UPLOAD_REQUIRED_DOCUMENT;
    } else if (isFrontlineEmployeeUser()) {
      URL = `${BASE_URL}hrm/file-management/upload-required-document`;
    } else {
      URL = `${BASE_URL}sp/file-management/upload-required-document`;
    }

    apiCall
      .post({
        url: URL,
        params: data,
        isAuthToken: currentLoginUser?.AccessToken,
      })
      .then(async (response) => {
        // Comment is already included in upload-required-document payload
        // Only call separate comment API if comment wasn't saved via upload endpoint
        if (allowDocumentComments && canEditComments && comment && response?.data?.data?.uuid) {
          try {
            // Small delay to ensure upload is processed first
            await new Promise(resolve => setTimeout(resolve, 500));
            await apiPostRequest("/hrm/employee-document/comment", {
              document_uuid: response.data.data.uuid,
              comment: comment,
            });
          } catch (commentError) {
            console.error("Error saving comment via API:", commentError);
            // Don't fail the upload if comment save fails, just log it
            // Comment might already be saved via upload endpoint
          }
        }
        handleFinish(response, {
          successMessage: "Document uploaded successfully",
        });
        // Clear comment field after successful upload
        setComment("");
        handleClose();
        setIsLoading(false);
        if (!roleAccess([USER_ROLE.Sp_Standard_User])) {
          // setUpdateDocumentsList(!updateDocumentsList);
          getEmployeeDocuments("from-uploaded");
        }
        // else if (roleAccess([USER_ROLE.Sp_Standard_User])) {
        //   sleep(1000).then(() => {
        //     window.location.reload();
        //   });
        // }
      })
      .catch((e) => {
        console.log("e", e);
        setIsLoading(false);
        toast.error(e?.message ?? "Something Went Wrong!");
        setErrorUploadAPI(e?.message ?? "Something Went Wrong!");
        setIsFileUploadToS3(false);
      });
  };

  const postRequiredDocumentDatesOnly = () => {
    const payloadDates = resolvePayloadDates();
    if (requiresIssueDate && isComplianceDateEmpty(issueDate)) {
      setIsLoading(false);
      toast.error("Issue Date is required.");
      return;
    }
    if (requiresExpiryDate && isComplianceDateEmpty(expiryDate)) {
      setIsLoading(false);
      toast.error("Expiry Date is required.");
      return;
    }

    const data = {
      worker_id: workerId,
      file_type: "Screening",
      document_type_id: 2,
      label: "",
      note: "xyz",
      name: (SelectedFileName || "").trim(),
      folder_structure_id: parentId,
      score: fileScore,
      ...payloadDates,
    };

    if (allowDocumentComments && canEditComments && comment) {
      Object.assign(data, {
        comment: comment,
        commented_by: currentLoginUser?.uuid || currentLoginUser?.id || null,
        commented_at: moment().toISOString(),
      });
    }

    if (selectedFile && selectedFile?.docuemnt_id) {
      Object.assign(data, {
        document_id: selectedFile.docuemnt_id,
      });
    }

    let URL = "";
    if (role === "participant") {
      URL =
        isFrontlineEmployeeUser()
          ? URL_HRM_PARTICIPANT_UPLOAD_REQUIRED_DOCUMENT
          : URL_SP_PARTICIPANT_UPLOAD_REQUIRED_DOCUMENT;
      data.pt_id = workerId;
      delete data.worker_id;
    } else {
      URL =
        isFrontlineEmployeeUser()
          ? `${BASE_URL}hrm/file-management/upload-required-document`
          : `${BASE_URL}sp/file-management/upload-required-document`;
    }

    apiCall
      .post({
        url: URL,
        params: data,
        isAuthToken: currentLoginUser?.AccessToken,
      })
      .then(async (response) => {
        if (
          allowDocumentComments &&
          canEditComments &&
          comment &&
          response?.data?.data?.uuid
        ) {
          try {
            await new Promise((resolve) => setTimeout(resolve, 500));
            await apiPostRequest("/hrm/employee-document/comment", {
              document_uuid: response.data.data.uuid,
              comment: comment,
            });
          } catch (commentError) {
            console.error("Error saving comment via API:", commentError);
          }
        }
        handleFinish(response, {
          successMessage: "Document updated successfully",
        });
        handleClose();
        if (
          !roleAccess([USER_ROLE.Sp_Standard_User]) &&
          getEmployeeDocuments
        ) {
          getEmployeeDocuments("from-uploaded");
        }
      })
      .catch((e) => {
        console.log("e", e);
        setIsLoading(false);
        toast.error(e?.message ?? "Something Went Wrong!");
        setErrorUploadAPI(e?.message ?? "Something Went Wrong!");
      });
  };

  const handleClear = () => {
    if (!hasComplianceDateRequirements || isParticipantCompliance) {
      setShowInput(false);
    }
    setFile("");
    setSelectedDocument("");
    setComment("");
    setIssueDate(null);
    setExpiryDate(null);
    setHasAttemptedSubmit(false);
    setIssueDateTouched(false);
    setExpiryDateTouched(false);
    setExistingAttachmentRemoved(false);
  };

  const handleFinish = (data, options = {}) => {
    if (data && data.data && data.data.file_type) {
      setUpdatedFile({ data });
      handleClose();
      handleClear();
    }
    handleClear();
    setIsLoading(false);
    setIsFileUploadToS3(false);
    toast.success(
      options.successMessage || "Document uploaded successfully"
    );
    if (
      role !== "participant" &&
      !roleAccess([USER_ROLE.Sp_Standard_User])
    ) {
      WorkerProfileDataRequest({
        profile_id:
          state && state?.workerUUID
            ? state?.workerUUID
            : params && params.workerId
            ? params?.workerId
            : workerId,
      });
    }
    if (getEmployeeDocuments) {
      getEmployeeDocuments("from-required-document-change");
    }
    if (getDocumentsListForProgressBar) {
      getDocumentsListForProgressBar();
    }
  };

  const handleError = () => {
    setIsLoading(false);
  };

  const handleSubmitForFile = async () => {
    if (isUserGracePeriodOver) {
      return;
    }

    setHasAttemptedSubmit(true);

    if (error && error?.length > 0) {
      return;
    }

    if (requiresIssueDate && isComplianceDateEmpty(issueDate)) {
      return;
    }
    if (requiresExpiryDate && isComplianceDateEmpty(expiryDate)) {
      return;
    }

    if (mandatoryFileMissingAfterRemoval) {
      toast.error(
        "The mandatory document must be uploaded before updating."
      );
      return;
    }

    const isDatesOnlyUpdate =
      Boolean(selectedFile?.docuemnt_id) && (file == null || file === "");

    if (isDatesOnlyUpdate) {
      setIsLoading(true);
      postRequiredDocumentDatesOnly();
      return;
    }

    setIsLoading(true);
    setIsFileUploadToS3(true);
  };

  const isDatesOnlyUpdate =
    canSaveDatesWithoutNewFile && (file == null || file === "");

  return (
    <ModalSkeleton
      title={
        isLoading
          ? isDatesOnlyUpdate
            ? "Updating Document"
            : "Uploading Document"
          : "Upload Document"
      }
      cssClass='w-[35%]'
      modalFooter={modalFooter()}
      isOpen={isOpen}
      closeModal={() => {
        handleClose();
        setShowInput(false);
        setFile("");
        setSelectedDocument("");
        setComment("");
        setIssueDate(null);
        setExpiryDate(null);
      }}
    >
      <div className='flex flex-col'>
        <div className='grid grid-cols-2 gap-x-4'>
          {showOptionalDateCheckbox && (
            <div className='col-span-2 space-x-4'>
              <label className=' text-start did-floating-label'>
                Set Issue/Expiry Date
              </label>
              <input
                checked={showInput}
                value={showInput}
                type='checkbox'
                onChange={(e) => {
                  const checked = e.target.checked;
                  setShowInput(checked);
                  if (!checked && !hasComplianceDateRequirements) {
                    setIssueDate(null);
                    setExpiryDate(null);
                    setIssueDateTouched(false);
                    setExpiryDateTouched(false);
                  }
                }}
                className='rounded '
              />
            </div>
          )}
          {showIssueDateField && (
            <div className='col-span-2'>
              <label htmlFor='issue_date' className=' text-start did-floating-label'>
                Issue Date
                {requiresIssueDate && (
                  <span className='text-red-500 ml-1' title='Required'>*</span>
                )}
              </label>
              <DateRangePickerNew
                  isSingleDate
                  isClearable
                  handleCancel={() => {
                    setIssueDateTouched(true);
                    setIssueDate(null);
                  }}
                  handleChange={(obj) => {
                    setIssueDateTouched(true);
                    setIssueDate(obj);
                  }}
                  singleSelectedDate={issueDateValue}
                  showCalendarIcon
                  preventOpenOnFocus
                  maxDate={moment().endOf("day").toDate()}
              />
              {showIssueDateRequiredError && (
                <span className='errorMessage'>Issue Date is required.</span>
              )}
            </div>
          )}
          {showExpiryDateField && (
            <div className='col-span-2'>
              <label htmlFor='expiry_date' className=' text-start did-floating-label'>
                Expiry Date
                {requiresExpiryDate && (
                  <span className='text-red-500 ml-1' title='Required'>*</span>
                )}
              </label>
              <DateRangePickerNew
                  isSingleDate
                  isClearable
                  handleCancel={() => {
                    setExpiryDateTouched(true);
                    setExpiryDate(null);
                  }}
                  handleChange={(obj) => {
                    setExpiryDateTouched(true);
                    setExpiryDate(obj);
                  }}
                  singleSelectedDate={expiryDateValue}
                  showCalendarIcon
                  preventOpenOnFocus
                  minDate={minExpiryDate}
              />
              {showExpiryDateRequiredError && (
                <span className='errorMessage'>Expiry Date is required.</span>
              )}
            </div>
          )}

          {!selectedDocument && (
            <div className='col-span-2 mt-4'>
              <FileUploadInput
                type='any'
                handleFile={(e) => {
                  setFile(e);
                  setExistingAttachmentRemoved(false);
                  if (error) setError("");
                }}
                size={10}
                file={file}
                handleDelete={() => {
                  setFile("");
                  if (existingUploadedFile?.link) {
                    setExistingAttachmentRemoved(true);
                  }
                }}
                isDisabled={isLoading}
                isVapourFileUpload={true}
                isFileUploadToS3={isFileUploadToS3}
                handleFileUploadBaseVapour={handleFileUploadBaseVapour}
                handleError={handleError}
                updateFileName={
                  existingUploadedFile?.link ? existingUploadedFileName : null
                }
                isHardClearAllStates={errorUploadAPI?.length > 0}
                clearParentError={() => setErrorUploadAPI("")}
                selectedFile={existingUploadedFile}
              />
              {showMandatoryFileRequiredError && (
                <span className='errorMessage mt-2 block'>
                  The mandatory document must be uploaded before updating.
                </span>
              )}
            </div>
          )}
          {allowDocumentComments && (
            <div className='col-span-2 mt-4'>
              <label htmlFor='comment' className='block text-sm font-medium text-gray-700 mb-1'>
                Comment <span className='text-gray-400 text-xs'>(Optional)</span>
                {!canEditComments && (
                  <span className='ml-2 text-xs text-gray-500 italic'>
                    (Only SP Admin/Manager can add comments)
                  </span>
                )}
              </label>
              <textarea
                id='comment'
                name='comment'
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={canEditComments ? 'Add comment...' : 'Comments disabled for your role'}
                rows={3}
                className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                  !canEditComments ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                disabled={isLoading || !canEditComments}
              />
              {canEditComments && (
                <p className='mt-1 text-xs text-gray-500'>
                  Add contextual notes or remarks for this document
                </p>
              )}
            </div>
          )}
          {error && error?.length > 0 && (
            <div className='col-span-2 mt-1'>
              <span className='errorMessage'>{error}</span>
            </div>
          )}
        </div>
      </div>
    </ModalSkeleton>
  );
};

const mapDispatchToProps = {
  WorkerProfileDataRequest: workerProfileDetailsFetchRequest,
};
const mapStateToProps = (state) => {
  const currentLoginUser = getUser(state);

return { currentLoginUser };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UploadFileTableFolder);
